import { useEffect, useEffectEvent, useState } from "react";
import { useRelayEnvironment } from "react-relay";
import { fetchQuery, type GraphQLTaggedNode, type OperationType, type Subscription } from "relay-runtime";

export type PollDecision<TResult> = { type: "continue"; cancelDeadline?: boolean } | { type: "complete"; result: TResult };

export type PollingConnectionState = "online" | "unavailable" | "recovering";

type PollingStatus = {
	connection: PollingConnectionState;
	isDelayed: boolean;
};

type UsePollingQueryProps<TQuery extends OperationType, TResult> = {
	query: GraphQLTaggedNode;
	variables: TQuery["variables"];
	createEvaluator: () => (data: TQuery["response"]) => PollDecision<TResult>;
	onComplete?: (result: TResult) => void;
	onData?: (data: TQuery["response"]) => void;
	intervalMs: number;
	minTimeMs?: number;
	delayedMs?: number;
	deadline?: { ms: number; result: TResult };
	enabled?: boolean;
	sessionKey?: string;
	fetcher?: () => Promise<TQuery["response"]>;
};

export function getPollingRetryDelay(intervalMs: number, consecutiveErrors: number) {
	return Math.min(Math.max(intervalMs, 1000) * 2 ** Math.max(consecutiveErrors - 1, 0), 5000);
}

export function getRemainingPollingTime(remainingMs: number | undefined, startedAt: number | undefined, now: number) {
	if (remainingMs === undefined || startedAt === undefined) return remainingMs;
	return Math.max(remainingMs - (now - startedAt), 0);
}

export function usePollingQuery<TQuery extends OperationType, TResult>(props: UsePollingQueryProps<TQuery, TResult>): PollingStatus {
	const { query, variables, intervalMs, minTimeMs = 0, delayedMs, deadline, enabled = true, sessionKey = "default" } = props;
	const environment = useRelayEnvironment();
	const createEvaluator = useEffectEvent(props.createEvaluator);
	const onComplete = useEffectEvent((result: TResult) => props.onComplete?.(result));
	const onData = useEffectEvent((data: TQuery["response"]) => props.onData?.(data));
	const fetcher = useEffectEvent(() => props.fetcher?.());
	const [status, setStatus] = useState<PollingStatus>(() => ({
		connection: navigator.onLine ? "online" : "unavailable",
		isDelayed: false,
	}));

	if (intervalMs <= 0) throw new Error("Polling interval must be greater than 0");

	const variablesKey = JSON.stringify(variables);
	const deadlineMs = deadline?.ms;
	const deadlineResult = deadline?.result;

	useEffect(() => {
		if (!enabled) return;

		let stopped = false;
		let finished = false;
		let deadlineActive = deadlineMs !== undefined || delayedMs !== undefined;
		let pollTimer: ReturnType<typeof setTimeout> | undefined;
		let delayedTimer: ReturnType<typeof setTimeout> | undefined;
		let deadlineTimer: ReturnType<typeof setTimeout> | undefined;
		let completionTimer: ReturnType<typeof setTimeout> | undefined;
		let subscription: Subscription | undefined;
		let consecutiveErrors = 0;
		let deadlineRemainingMs = deadlineMs;
		let delayedRemainingMs = delayedMs;
		let deadlineStartedAt: number | undefined;
		let delayedStartedAt: number | undefined;
		const startedAt = Date.now();
		const evaluate = createEvaluator();

		const clearPoll = () => {
			if (pollTimer) clearTimeout(pollTimer);
			subscription?.unsubscribe();
		};
		const stopDeadlineTimers = () => {
			if (deadlineTimer) clearTimeout(deadlineTimer);
			if (delayedTimer) clearTimeout(delayedTimer);
			deadlineTimer = undefined;
			delayedTimer = undefined;
		};
		const pauseDeadline = () => {
			const now = Date.now();
			deadlineRemainingMs = getRemainingPollingTime(deadlineRemainingMs, deadlineStartedAt, now);
			delayedRemainingMs = getRemainingPollingTime(delayedRemainingMs, delayedStartedAt, now);
			deadlineStartedAt = undefined;
			delayedStartedAt = undefined;
			stopDeadlineTimers();
		};
		const clearDeadline = () => {
			deadlineActive = false;
			stopDeadlineTimers();
			setStatus((current) => (current.isDelayed ? { ...current, isDelayed: false } : current));
		};
		const finish = (result: TResult) => {
			if (stopped || finished) return;
			finished = true;
			clearPoll();
			clearDeadline();
			completionTimer = setTimeout(() => onComplete(result), Math.max(minTimeMs - (Date.now() - startedAt), 0));
		};
		const handleResponse = (response: TQuery["response"]) => {
			if (stopped || finished) return;
			consecutiveErrors = 0;
			setStatus((current) => (current.connection === "online" ? current : { ...current, connection: "online" }));
			onData(response);
			const decision = evaluate(response);
			if (decision.type === "complete") {
				finish(decision.result);
				return;
			}
			if (decision.cancelDeadline && deadlineActive) clearDeadline();
			schedule();
		};
		const handleError = () => {
			if (stopped || finished) return;
			setStatus((current) => ({ ...current, connection: "unavailable" }));
			consecutiveErrors += 1;
			if (navigator.onLine) schedule(getPollingRetryDelay(intervalMs, consecutiveErrors));
		};
		const schedule = (nextDelay = intervalMs) => {
			pollTimer = setTimeout(run, nextDelay);
		};
		const startDeadline = () => {
			if (!deadlineActive) return;
			pauseDeadline();
			setStatus((current) => (current.isDelayed ? { ...current, isDelayed: false } : current));
			const now = Date.now();
			if (delayedRemainingMs !== undefined) {
				delayedStartedAt = now;
				delayedTimer = setTimeout(() => {
					delayedRemainingMs = 0;
					delayedStartedAt = undefined;
					setStatus((current) => ({ ...current, isDelayed: true }));
				}, delayedRemainingMs);
			}
			if (deadlineRemainingMs !== undefined && deadlineResult !== undefined) {
				deadlineStartedAt = now;
				deadlineTimer = setTimeout(() => finish(deadlineResult), deadlineRemainingMs);
			}
		};
		const run = () => {
			if (stopped || finished) return;
			const rawRequest = fetcher();
			if (rawRequest) {
				rawRequest.then(handleResponse).catch(handleError);
				return;
			}
			let response: TQuery["response"] | undefined;
			subscription = fetchQuery<TQuery>(environment, query, variables, { fetchPolicy: "network-only" }).subscribe({
				next: (data) => {
					response = data;
				},
				complete: () => response && handleResponse(response),
				error: handleError,
			});
		};
		const reconnect = () => {
			if (stopped || finished) return;
			setStatus((current) => ({ ...current, connection: "recovering" }));
			clearPoll();
			startDeadline();
			run();
		};
		const handleVisibility = () => {
			if (document.visibilityState === "hidden") {
				clearPoll();
				pauseDeadline();
				return;
			}
			if (navigator.onLine) reconnect();
		};
		const pause = () => {
			setStatus((current) => ({ ...current, connection: "unavailable" }));
			clearPoll();
			pauseDeadline();
		};

		setStatus({ connection: navigator.onLine ? "online" : "unavailable", isDelayed: false });
		window.addEventListener("online", reconnect);
		window.addEventListener("offline", pause);
		document.addEventListener("visibilitychange", handleVisibility);
		if (navigator.onLine) {
			startDeadline();
			run();
		}

		return () => {
			stopped = true;
			clearPoll();
			stopDeadlineTimers();
			if (completionTimer) clearTimeout(completionTimer);
			window.removeEventListener("online", reconnect);
			window.removeEventListener("offline", pause);
			document.removeEventListener("visibilitychange", handleVisibility);
		};
		// GraphQL variables are serializable and variablesKey changes when their values change.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [environment, query, variablesKey, intervalMs, minTimeMs, delayedMs, deadlineMs, enabled, sessionKey]);

	return status;
}
